import React from 'react';
import { HumanChatMessageBody } from '~/chat/components/human-chat-message-body/HumanChatMessageBody';
import type { ChatMessage as ChatMessageType } from '../../hooks/useChatWithPowerSync';
import { View } from 'react-native';
import { TextClassContext } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import { Markdown } from '~/extension/components/markdown/Markdown';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import type { ChatMessagePartRecord } from '~/powersync/generated/schema';
import { StructuredPartRenderer } from './StructuredPartRenderer';

export interface ChatMessageProps {
  message: ChatMessageType;
  parts: ChatMessagePartRecord[];
  /** Smaller type for the right-rail chat. */
  compact?: boolean;
}

export const ChatMessage: React.FC<ChatMessageProps> & {
  Synced: typeof ChatMessageSynced;
} = ({ message, parts, compact }) => {
  if (message.role === 'user') {
    // User messages: render first text part or fallback
    const textPart = parts.find((p) => p.type === 'text');
    return (
      <HumanChatMessageBody content={textPart?.text || ''} compact={compact} />
    );
  }

  // Assistant messages: render all parts in order
  if (parts.length === 0) {
    return null;
  }

  return (
    <View className="w-full py-2 gap-3">
      {parts.map((part) => {
        if (part.type === 'text') {
          return (
            <View
              key={part.id}
              className={cn(
                'rounded-2xl p-3.5 self-start max-w-[85%]',
                // Right-rail variant: a white bubble instead of the surface one.
                compact ? 'bg-background' : 'bg-surface',
              )}
            >
              <TextClassContext.Provider
                value={compact ? 'text-sm' : undefined}
              >
                <Markdown>{part.text || ''}</Markdown>
              </TextClassContext.Provider>
            </View>
          );
        }

        if (part.type === 'structured' && part.kind) {
          return (
            <StructuredPartRenderer
              key={part.id}
              kind={part.kind}
              payload={part.payloadJson ? JSON.parse(part.payloadJson) : {}}
            />
          );
        }

        return null;
      })}
    </View>
  );
};

interface ChatMessageSyncedProps {
  message: ChatMessageType;
  compact?: boolean;
}

const ChatMessageSynced: React.FC<ChatMessageSyncedProps> = ({
  message,
  compact,
}) => {
  // Fetch parts for this message
  const { data: parts, isLoading } =
    useLivePowerSyncQuery<ChatMessagePartRecord>(
      db.Chat.queries.findMessageParts(message.id),
      [message.id],
    );

  if (isLoading || !parts) {
    return null;
  }

  return <ChatMessage message={message} parts={parts} compact={compact} />;
};

// Attach Synced as a static property
ChatMessage.Synced = ChatMessageSynced;
